import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import api from '../services/api';
import { toast } from 'react-toastify';

function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  // Check if user is already logged in
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      // If there's a redirect path in the URL, use it. Otherwise, go to dashboard
      const from = location.state?.from?.pathname || '/dashboard';
      navigate(from, { replace: true });
    }
  }, [navigate, location.state]);

  const validateForm = () => {
    const newErrors = {};

    if (!email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!password) {
      newErrors.password = "Password is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsLoading(true);

    try {
      // Admins authenticate against the dedicated admins collection.
      const response = await api.post('/v1/admin/auth/login', {
        email: email,
        password: password
      });

      // Handle different response formats
      const responseData = response.data;

      if (!responseData) {
        throw new Error('Empty response from server');
      }

      // Check for token in different possible locations (including accessToken)
      const token = responseData.accessToken ||
        responseData.token ||
        responseData.data?.accessToken ||
        responseData.data?.token ||
        responseData.access_token ||
        responseData.data?.access_token;

      if (!token) {
        throw new Error('Authentication failed: No token received');
      }

      // Try to extract user data if available
      const userData = responseData.user ||
        responseData.data?.user ||
        { email };

      // Check if user has admin or super_admin role
      if (userData.role !== 'admin' && userData.role !== 'super_admin') {
        console.error('Access denied: User role is', userData.role);
        toast.error('Access denied. Only adminis can login to this dashboard.');
        setIsLoading(false);
        return;
      }

      // Store the token (only after role validation)
      localStorage.setItem('token', token);

      if (userData) {
        localStorage.setItem('user', JSON.stringify(userData));
      }

      // Check for OTP requirement
      const requiresOtp = responseData.requiresOtp ||
        responseData.data?.requiresOtp ||
        responseData.twoFactorRequired;

      if (requiresOtp) {
        console.log('OTP required, redirecting...');
        navigate(`/otp?email=${encodeURIComponent(email)}`);
      } else {
        console.log('Login successful, redirecting to dashboard...');
        const from = location.state?.from?.pathname || '/dashboard';
        navigate(from, { replace: true });
      }

    } catch (error) {
      console.error('Login failed:', error.response?.status, error.message);

      let errorMessage = 'Login failed. Please try again.';

      if (error.response) {
        // Check for authentication failure (401 Unauthorized)
        if (error.response.status === 401) {
          errorMessage = 'Invalid credentials. Please check your email and password.';
        } else {
          errorMessage = error.response.data?.message ||
            error.response.data?.error ||
            error.response.statusText ||
            `Server responded with status ${error.response.status}`;
        }
      } else if (error.request) {
        errorMessage = 'No response from server. Please check your connection.';
      }

      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex h-screen items-center justify-center">
      <div className="flex w-full max-w-5xl min-h-[600px] rounded-xl shadow-lg overflow-hidden">
        {/* Left gray block */}
        <div className="hidden md:flex w-2/5 bg-gray-200 relative overflow-hidden">
          <img
            src="/loginImage.png"
            alt="Login illustration"
            className="w-full h-full object-cover"
          />
        </div>

        {/* Right login card */}
        <div className="flex flex-1 items-center justify-center p-8">
          <div className="w-full max-w-sm">
            <h2
              className="text-gray-700 text-2xl font-bold mb-2"
              style={{ fontFamily: "Montserrat" }}
            >
              Welcome Back
            </h2>
            <p className="text-gray-500 text-sm mb-6">
              Login to your admin account
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2">
                  Email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FFA500] focus:outline-none"
                  required
                  disabled={isLoading}
                />
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2">
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FFA500] focus:outline-none pr-10"
                    required
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={isLoading}
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5 text-gray-400" />
                    ) : (
                      <Eye className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                </div>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-2 rounded-lg text-sm">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={isLoading}
                className={`w-full bg-[#FFA500] text-black py-2 rounded-lg font-medium hover:bg-orange-400 transition flex items-center justify-center ${isLoading ? 'opacity-75 cursor-not-allowed' : ''
                  }`}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="animate-spin mr-2 h-4 w-4" />
                    Logging in...
                  </>
                ) : (
                  'Login'
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;